import { useEffect, useRef } from "react";
import { X, Plus } from "lucide-react";
import { colorFor } from "../shared";
import { devLog } from "@/lib/log";

const DOUBLE_CLICK_MS = 260;

/**
 * Side drawer that opens when an admin clicks "+N more" on a packed day cell
 * (or the day-number button). Lists every slot on that ISO date so nothing
 * is hidden behind the 3-event preview in the grid.
 */
const DayEventsDrawer = ({
  iso,
  events,
  onClose,
  onOpenEvent,
  onDoubleClickEvent,
  onAddSlotForDay,
}) => {
  // Same click-delay pattern as the calendar grid so a single click
  // opens details and a double-click routes to onDoubleClickEvent
  // without the details modal opening on the first click.
  const clickTimers = useRef({});
  const handleEventClick = (e) => {
    const pending = clickTimers.current[e.id];
    if (pending) {
      clearTimeout(pending);
      delete clickTimers.current[e.id];
      if (onDoubleClickEvent) onDoubleClickEvent(e);
      return;
    }
    clickTimers.current[e.id] = setTimeout(() => {
      delete clickTimers.current[e.id];
      onOpenEvent(e);
    }, DOUBLE_CLICK_MS);
  };
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const dateLabel = (() => {
    try {
      return new Date(iso + "T12:00:00").toLocaleDateString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch (e) {
      devLog("ld_calendar dateLabel format failed:", e?.message);
      return iso;
    }
  })();

  // Sort chronologically — time is "HH:MM" so a lexical sort works.
  const sorted = [...events].sort((a, b) =>
    (a.time || "").localeCompare(b.time || "")
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="cal-day-drawer"
      className="fixed inset-0 z-50 bg-[var(--ld-ink)]/60 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <aside
        className="w-full max-w-md bg-[var(--ld-ivory)] border-l border-[var(--ld-border)] h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 p-5 border-b border-[var(--ld-border)]">
          <div className="min-w-0">
            <span className="font-accent text-[10px] tracking-[0.25em] uppercase text-[var(--ld-gold-deep)]">
              All slots
            </span>
            <h3 className="font-serif-display text-xl mt-1 text-[var(--ld-ink)]">
              {dateLabel}
            </h3>
            <p className="text-[11px] text-[var(--ld-ink-muted)] mt-1">
              {events.length} slot{events.length === 1 ? "" : "s"} on this day
            </p>
          </div>
          <button
            type="button"
            data-testid="cal-day-drawer-close"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center border border-[var(--ld-ink)]/20 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)]"
            aria-label="Close"
          >
            <X size={14} strokeWidth={1.6} />
          </button>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-2">
          {sorted.length === 0 && (
            <p className="italic text-[13px] text-[var(--ld-ink-muted)]">
              No slots scheduled.
            </p>
          )}
          {sorted.map((e) => {
            const c = colorFor(e);
            return (
              <button
                key={e.id}
                type="button"
                data-testid={`cal-day-drawer-event-${e.id}`}
                onClick={() => handleEventClick(e)}
                title={`${e.title || c.label} · double-click to mark booked`}
                className={
                  "w-full text-left px-3 py-3 border-l-4 flex items-start gap-3 hover:opacity-90 transition-opacity " +
                  c.card
                }
              >
                <span className="font-accent text-[11px] tracking-[0.18em] uppercase shrink-0 tabular-nums">
                  {e.time || "—"}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[13px] truncate">
                    {e.title || c.label}
                  </div>
                  {e.duration_minutes ? (
                    <div className="text-[11px] opacity-75 mt-0.5">
                      {e.duration_minutes} min · {c.label}
                    </div>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
        <footer className="border-t border-[var(--ld-border)] p-4">
          <button
            type="button"
            data-testid="cal-day-drawer-add"
            onClick={() => onAddSlotForDay(iso)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] hover:bg-black transition-colors"
          >
            <Plus size={13} strokeWidth={1.7} />
            Add slot to this day
          </button>
        </footer>
      </aside>
    </div>
  );
};

export default DayEventsDrawer;
